import { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle,
  Shield,
  Flame,
  Zap,
  Car,
  HelpCircle,
  X,
  CheckCircle,
  Loader2,
  Clock,
  User,
  ChevronRight,
  Phone,
  Stethoscope,
  Building2,
} from 'lucide-react';
import { accessApi } from '../services/accessApi';
import { emergencyNumbersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../lib/utils';

type AlertType = 'robo' | 'incendio' | 'apagon' | 'accidente' | 'otro';

interface AlertItem {
  id: string;
  alertType: AlertType;
  status: 'active' | 'responded' | 'resolved';
  notes?: string;
  createdAt: string;
  respondedAt?: string;
  resolvedAt?: string;
  userName?: string;
  unit?: string;
  resolution?: string;
}

const alertTypes: { value: AlertType; label: string; icon: typeof Shield; color: string; bg: string }[] = [
  { value: 'robo', label: 'Robo', icon: Shield, color: 'text-red-600', bg: 'bg-red-100' },
  { value: 'incendio', label: 'Incendio', icon: Flame, color: 'text-orange-600', bg: 'bg-orange-100' },
  { value: 'apagon', label: 'Apagón', icon: Zap, color: 'text-yellow-600', bg: 'bg-yellow-100' },
  { value: 'accidente', label: 'Accidente', icon: Car, color: 'text-blue-600', bg: 'bg-blue-100' },
  { value: 'otro', label: 'Otro', icon: HelpCircle, color: 'text-slate-600', bg: 'bg-slate-100' },
];

const statusLabels: Record<string, string> = {
  active: 'Activa',
  // Backend uses 'attending'; UI also accepts the legacy 'responded'.
  attending: 'En atención',
  responded: 'En atención',
  resolved: 'Resuelta',
};

const statusColors: Record<string, string> = {
  active: 'bg-red-100 text-red-700',
  attending: 'bg-yellow-100 text-yellow-700',
  responded: 'bg-yellow-100 text-yellow-700',
  resolved: 'bg-green-100 text-green-700',
};

interface EmergencyContact {
  id: string;
  name: string;
  phone: string;
  category?: string;
}

// Visual config per emergency-contact category. Falls back to a generic icon
// for any category the backend returns that we don't explicitly know about.
const contactCategoryConfig: Record<string, { label: string; icon: typeof Shield; color: string; bg: string }> = {
  emergencia: { label: 'Emergencias', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-100' },
  policia: { label: 'Policía', icon: Shield, color: 'text-blue-600', bg: 'bg-blue-100' },
  bomberos: { label: 'Bomberos', icon: Flame, color: 'text-orange-600', bg: 'bg-orange-100' },
  ambulancia: { label: 'Ambulancia', icon: Stethoscope, color: 'text-rose-600', bg: 'bg-rose-100' },
  salud: { label: 'Salud', icon: Stethoscope, color: 'text-rose-600', bg: 'bg-rose-100' },
  seguridad: { label: 'Seguridad interna', icon: Building2, color: 'text-emerald-600', bg: 'bg-emerald-100' },
  servicios: { label: 'Servicios', icon: Phone, color: 'text-slate-600', bg: 'bg-slate-100' },
  otros: { label: 'Otros', icon: HelpCircle, color: 'text-slate-600', bg: 'bg-slate-100' },
};

const getContactConfig = (category?: string) =>
  (category && contactCategoryConfig[category]) || {
    label: category || 'Contacto',
    icon: Phone,
    color: 'text-slate-600',
    bg: 'bg-slate-100',
  };

// Normalize a raw alert (snake_case from the API) into the AlertItem shape
// the UI expects, so timestamps and types render correctly.
function normalizeAlert(raw: any): AlertItem {
  return {
    id: String(raw?.id ?? ''),
    alertType: (raw?.alertType ?? raw?.alert_type ?? 'otro') as AlertType,
    status: (raw?.status ?? 'active') as AlertItem['status'],
    notes: raw?.notes ?? undefined,
    createdAt: raw?.createdAt ?? raw?.created_at ?? '',
    respondedAt: raw?.respondedAt ?? raw?.responded_at ?? undefined,
    resolvedAt: raw?.resolvedAt ?? raw?.resolved_at ?? undefined,
    userName: raw?.userName ?? raw?.user_name ?? raw?.name ?? undefined,
    unit: raw?.unit ?? raw?.unit_label ?? undefined,
    resolution: raw?.resolution ?? undefined,
  };
}

export default function PanicButton() {
  const { user } = useAuth();
  const isAdminOrSecurity = user && ['admin', 'super_admin', 'security', 'directive'].includes(user.role);

  const [stage, setStage] = useState<'idle' | 'select' | 'sending' | 'sent'>('idle');
  const [selectedType, setSelectedType] = useState<AlertType | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Admin/security state
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);

  // Emergency contacts (shown to everyone)
  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  const fetchAlerts = useCallback(async () => {
    if (!isAdminOrSecurity) return;
    setAlertsLoading(true);
    try {
      const res = await accessApi.panic.active();
      const payload = res.data?.data ?? res.data;
      const list = payload?.alerts || payload?.data?.alerts || (Array.isArray(payload) ? payload : []);
      setAlerts(Array.isArray(list) ? list.map(normalizeAlert) : []);
    } catch {
      setAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
  }, [isAdminOrSecurity]);

  const fetchContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = userData.condoId || (user as any)?.condoId || (user as any)?.condo_id;
      if (!condoId) {
        setContacts([]);
        return;
      }
      const res = await emergencyNumbersAPI.getAll(condoId);
      // Backend responds with { success, data: rows }; axios wraps that in
      // res.data, so the array lives at res.data.data.
      const payload = res?.data ?? res;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.items)
            ? payload.items
            : [];
      setContacts(list as EmergencyContact[]);
    } catch {
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleCall = (phone: string) => {
    window.location.href = `tel:${String(phone).replace(/[^0-9+]/g, '')}`;
  };

  const handleTrigger = async () => {
    if (!selectedType) return;
    setStage('sending');
    setError(null);
    try {
      const position = await new Promise<GeolocationPosition | null>((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(resolve, () => resolve(null), { enableHighAccuracy: true, timeout: 4000 });
      });
      await accessApi.panic.trigger({
        condoId: user?.condoId || (user as any)?.condo_id || localStorage.getItem('condoId') || 'condo-default',
        alertType: selectedType,
        alert_type: selectedType,
        type: selectedType,
        locationLat: position?.coords.latitude,
        locationLng: position?.coords.longitude,
        notes: notes.trim() || undefined,
      });
      setStage('sent');
      if (isAdminOrSecurity) fetchAlerts();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Error al enviar alerta');
      setStage('select');
    }
  };

  const handleRespond = async (id: string) => {
    setActionId(id);
    try {
      await accessApi.panic.respond(id);
      fetchAlerts();
    } catch {
      // ignore
    } finally {
      setActionId(null);
    }
  };

  const handleResolve = async (id: string) => {
    if (!confirm('¿Marcar esta alerta como resuelta?')) return;
    setActionId(id);
    try {
      await accessApi.panic.resolve(id);
      fetchAlerts();
    } catch {
      // ignore
    } finally {
      setActionId(null);
    }
  };

  const reset = () => {
    setStage('idle');
    setSelectedType(null);
    setNotes('');
    setError(null);
  };

  return (
    <div className="min-h-full bg-[var(--v-base-bg)] p-4 md:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-8 h-8 text-red-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Botón de Pánico</h1>
            <p className="text-sm text-slate-500">Envía una alerta de emergencia</p>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <AlertTriangle className="w-5 h-5" />
            <span className="text-sm">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Panic Button Section */}
        <div className="bg-[var(--v-surface-1)] rounded-xl shadow-sm border border-slate-200 p-8">
          {stage === 'idle' && (
            <div className="flex flex-col items-center gap-6">
              <p className="text-center text-slate-600 max-w-md">
                Mantén presionado para enviar una alerta de emergencia a seguridad y administración
              </p>
              <button
                onClick={() => setStage('select')}
                className="w-48 h-48 rounded-full bg-gradient-to-b from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 shadow-lg shadow-red-200 hover:shadow-xl hover:shadow-red-300 transition-all flex items-center justify-center group active:scale-95"
              >
                <div className="flex flex-col items-center gap-2 text-white">
                  <AlertTriangle className="w-16 h-16 group-hover:scale-110 transition-transform" />
                  <span className="text-xl font-bold uppercase tracking-wide">Pánico</span>
                </div>
              </button>
            </div>
          )}

          {stage === 'select' && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-bold text-slate-900">Selecciona el tipo de emergencia</h2>
                <p className="text-sm text-slate-500 mt-1">Esta acción notificará inmediatamente</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {alertTypes.map(({ value, label, icon: Icon, color, bg }) => (
                  <button
                    key={value}
                    onClick={() => setSelectedType(value)}
                    className={`flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all ${
                      selectedType === value
                        ? 'border-red-500 bg-red-50 scale-105'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className={`w-14 h-14 rounded-full ${bg} flex items-center justify-center`}>
                      <Icon className={`w-7 h-7 ${color}`} />
                    </div>
                    <span className="font-medium text-slate-900">{label}</span>
                  </button>
                ))}
              </div>

              {selectedType && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Notas (opcional)</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Describe la emergencia..."
                    />
                  </div>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={handleTrigger}
                      disabled={(stage as string) === 'sending'}
                      className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-8 py-3 rounded-lg font-bold transition-colors"
                    >
                      {(stage as string) === 'sending' ? (
                        <><Loader2 className="w-5 h-5 animate-spin" /> Enviando...</>
                      ) : (
                        <><AlertTriangle className="w-5 h-5" /> Confirmar Alerta</>
                      )}
                    </button>
                    <button
                      onClick={reset}
                      className="px-6 py-3 border border-slate-300 text-slate-600 rounded-lg font-medium hover:bg-slate-100"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {stage === 'sent' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Alerta enviada</h2>
              <p className="text-slate-500 text-center max-w-sm">
                Tu alerta de <span className="font-semibold capitalize">
                  {alertTypes.find((t) => t.value === selectedType)?.label}
                </span> ha sido enviada. Seguridad ha sido notificada.
              </p>
              <button
                onClick={reset}
                className="mt-4 px-6 py-2 border border-slate-300 text-slate-600 rounded-lg font-medium hover:bg-slate-100"
              >
                Cerrar
              </button>
            </div>
          )}
        </div>

        {/* Emergency Contacts (visible to everyone) */}
        <div className="bg-[var(--v-surface-1)] rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-red-600" />
              <h2 className="text-lg font-semibold text-slate-900">Contactos de emergencia</h2>
            </div>
            <button
              onClick={fetchContacts}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Actualizar
            </button>
          </div>

          {contactsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-8">
              <Phone className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500 text-sm">No hay contactos de emergencia registrados</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {contacts.map((contact) => {
                const cfg = getContactConfig(contact.category);
                const CIcon = cfg.icon;
                return (
                  <div
                    key={contact.id}
                    className="flex items-center justify-between bg-slate-50 rounded-lg p-4 border border-slate-200"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-10 h-10 rounded-lg ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
                        <CIcon className={`w-5 h-5 ${cfg.color}`} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{contact.name}</p>
                        <p className="text-xs text-slate-500">{cfg.label}</p>
                        <p className="text-sm text-slate-600">{contact.phone}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCall(contact.phone)}
                      className="flex items-center gap-1 text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg font-medium flex-shrink-0"
                    >
                      <Phone className="w-3 h-3" /> Llamar
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Admin/Security: Active Alerts */}
        {isAdminOrSecurity && (
          <div className="bg-[var(--v-surface-1)] rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Alertas activas</h2>
              <button
                onClick={fetchAlerts}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Actualizar
              </button>
            </div>

            {alertsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : alerts.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No hay alertas activas</p>
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert) => {
                  const typeConfig = alertTypes.find((t) => t.value === alert.alertType) || alertTypes[4];
                  const Icon = typeConfig.icon;
                  return (
                    <div
                      key={alert.id}
                      className="flex items-start justify-between bg-slate-50 rounded-lg p-4 border border-slate-200"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg ${typeConfig.bg} flex items-center justify-center flex-shrink-0`}>
                          <Icon className={`w-5 h-5 ${typeConfig.color}`} />
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900 capitalize">{typeConfig.label}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[alert.status]}`}>
                              {statusLabels[alert.status]}
                            </span>
                          </div>
                          {alert.userName && (
                            <div className="flex items-center gap-1 text-xs text-slate-500">
                              <User className="w-3 h-3" /> {alert.userName}
                              {alert.unit && <span>· {alert.unit}</span>}
                            </div>
                          )}
                          {alert.notes && <p className="text-sm text-slate-600">{alert.notes}</p>}
                          <div className="flex items-center gap-1 text-xs text-slate-400">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(alert.createdAt)}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        {alert.status === 'active' && (
                          <button
                            onClick={() => handleRespond(alert.id)}
                            disabled={actionId === alert.id}
                            className="flex items-center gap-1 text-sm bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1.5 rounded-lg font-medium disabled:opacity-50"
                          >
                            {actionId === alert.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronRight className="w-3 h-3" />}
                            Atender
                          </button>
                        )}
                        {alert.status !== 'resolved' && (
                          <button
                            onClick={() => handleResolve(alert.id)}
                            disabled={actionId === alert.id}
                            className="flex items-center gap-1 text-sm bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg font-medium disabled:opacity-50"
                          >
                            <CheckCircle className="w-3 h-3" />
                            Resolver
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
// force-reapply 1782311653
